import React, { useState, useRef, useEffect } from 'react';
import { useAuthContext } from '../../../context/AuthContext';
import apiService from '../../../services/api';
import { renderStars } from '../../../utils/helpers';
import styles from './ProductCard.module.css';

// ─── Constants ────────────────────────────────────────────────────────────────

const FALLBACK_IMAGE =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='300' viewBox='0 0 400 300'%3E%3Crect width='400' height='300' fill='%23f0f0f0'/%3E%3Crect x='150' y='90' width='100' height='80' rx='8' fill='%23ccc'/%3E%3Ccircle cx='175' cy='115' r='12' fill='%23aaa'/%3E%3Cpolygon points='150,170 190,130 220,155 250,125 280,170' fill='%23bbb'/%3E%3Ctext x='200' y='210' font-family='Arial,sans-serif' font-size='14' fill='%23999' text-anchor='middle'%3ENo Image%3C/text%3E%3C/svg%3E";

const BACKEND_HOST =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_BACKEND_URL)
    ? import.meta.env.VITE_BACKEND_URL
    : `http://${window.location.hostname}:8000`;

// ─── Helpers ──────────────────────────────────────────────────────────────────

function resolveUrl(url) {
  if (!url || typeof url !== 'string') return FALLBACK_IMAGE;
  if (url.startsWith('data:')) return url;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return `${BACKEND_HOST}${url.startsWith('/') ? '' : '/'}${url}`;
}

function isVideoUrl(url) {
  if (!url || typeof url !== 'string') return false;
  const lower = url.toLowerCase().split('?')[0];
  return ['.mp4', '.webm', '.ogg', '.mov', '.avi', '.mkv'].some(ext => lower.endsWith(ext));
}

// ✅ Picks the best media src — video takes priority over image
function resolveMediaSrc(product) {
  // 1. Check dedicated videos array (Business plan ads)
  const videos = product?.videos;
  if (Array.isArray(videos) && videos.length > 0) {
    const v = videos[0];
    const url = typeof v === 'string' ? v : v?.video || v?.url || v?.file || null;
    if (url) return { src: resolveUrl(url), isVideo: true };
  }

  // 2. Check primary_video field
  if (product?.primary_video) {
    return { src: resolveUrl(product.primary_video), isVideo: true };
  }

  // 3. Check legacy img/video field that might already be a video url
  const legacySrc = product?.img || product?.images?.[0];
  if (isVideoUrl(legacySrc)) {
    return { src: resolveUrl(legacySrc), isVideo: true };
  }

  // 4. Check images array — could contain a video url too
  const images = product?.images;
  if (Array.isArray(images)) {
    for (const img of images) {
      const url = typeof img === 'string' ? img : img?.image || img?.url || img?.file;
      if (isVideoUrl(url)) return { src: resolveUrl(url), isVideo: true };
    }
  }

  // 5. Fall back to image
  const imgSrc = product?.img
    || (Array.isArray(product?.images) && product.images.length > 0
        ? (typeof product.images[0] === 'string' ? product.images[0] : product.images[0]?.image || product.images[0]?.url)
        : null)
    || null;
  return { src: resolveUrl(imgSrc), isVideo: false };
}

function resolveRating(product) {
  const raw =
    product?.average_rating ??
    product?.avg_rating      ??
    product?.rating_avg      ??
    product?.rating          ??
    null;
  if (raw === null || raw === undefined) return null;
  const n = typeof raw === 'number' ? raw : parseFloat(raw);
  if (isNaN(n) || n <= 0) return null;
  return parseFloat(n.toFixed(1));
}

// Global shared audio state
const globalAudio = { muted: true, volume: 0.25 };

// ─── Component ────────────────────────────────────────────────────────────────

const ProductCard = ({ product, onClick }) => {
  const { isAuthenticated } = useAuthContext();

  // ── Media — video-first resolution ────────────────────────────────────────
  const { src: resolvedSrc, isVideo } = resolveMediaSrc(product);

  const videoRef  = useRef(null);
  const [isHovered, setIsHovered] = useState(false);
  const [isMuted,   setIsMuted]   = useState(globalAudio.muted);
  const [volume,    setVolume]    = useState(globalAudio.volume);
  const [imgSrc,    setImgSrc]    = useState(resolvedSrc);
  const [hasError,  setHasError]  = useState(false);

  // ── Likes ──────────────────────────────────────────────────────────────────
  const [isLiked,   setIsLiked]   = useState(product?.is_liked === true);
  const [likeCount, setLikeCount] = useState(product?.like_count || 0);
  const [isLiking,  setIsLiking]  = useState(false);
  const isLikingRef = useRef(false);

  useEffect(() => {
    setIsLiked(product?.is_liked === true);
    setLikeCount(product?.like_count || 0);
  }, [product?.is_liked, product?.like_count]);

  // ── Rating ─────────────────────────────────────────────────────────────────
  const [rating, setRating] = useState(() => resolveRating(product));
  useEffect(() => {
    setRating(resolveRating(product));
  }, [product?.average_rating, product?.avg_rating, product?.rating_avg, product?.rating, product?.id]);
  const ratingCount = product?.rating_count ?? product?.ratings_count ?? 0;

  // ── Sync video audio ───────────────────────────────────────────────────────
  useEffect(() => {
    if (!videoRef.current) return;
    videoRef.current.muted  = isMuted;
    videoRef.current.volume = volume;
  }, [isMuted, volume]);

  // ── Video hover ────────────────────────────────────────────────────────────
  const handleMouseEnter = () => {
    if (!isVideo) return;
    setIsMuted(globalAudio.muted);
    setVolume(globalAudio.volume);
    setIsHovered(true);
    if (videoRef.current) {
      videoRef.current.muted  = globalAudio.muted;
      videoRef.current.volume = globalAudio.volume;
      videoRef.current.play().catch(() => {});
    }
  };

  const handleMouseLeave = () => {
    if (!isVideo) return;
    setIsHovered(false);
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
  };

  const toggleMute = (e) => {
    e.stopPropagation();
    const next = !isMuted;
    setIsMuted(next);
    globalAudio.muted = next;
    if (videoRef.current) videoRef.current.muted = next;
  };

  const handleVolumeChange = (e) => {
    e.stopPropagation();
    const val = parseFloat(e.target.value);
    setVolume(val);
    globalAudio.volume = val;
    if (videoRef.current) videoRef.current.volume = val;
    if (val > 0 && isMuted) {
      setIsMuted(false);
      globalAudio.muted = false;
      if (videoRef.current) videoRef.current.muted = false;
    }
  };

  // ── Card click ─────────────────────────────────────────────────────────────
  const handleCardClick = (e) => {
    if (isLikingRef.current) return;
    if (onClick) onClick(e);
  };

  // ── Like ───────────────────────────────────────────────────────────────────
  const handleLikeClick = async (e) => {
    e.stopPropagation();
    e.nativeEvent?.stopImmediatePropagation?.();
    if (!isAuthenticated) { alert('Please log in to like this item'); return; }
    if (isLikingRef.current) return;

    isLikingRef.current = true;
    setIsLiking(true);

    const prevLiked = isLiked;
    const prevCount = likeCount;
    const optimisticLiked = !isLiked;
    const optimisticCount = optimisticLiked ? likeCount + 1 : Math.max(0, likeCount - 1);
    setIsLiked(optimisticLiked);
    setLikeCount(optimisticCount);

    try {
      const res = await apiService.toggleAdLike(product.id);
      if (res.success) {
        setIsLiked(res.is_liked === true);
        setLikeCount(res.like_count ?? optimisticCount);
      } else {
        setIsLiked(prevLiked);
        setLikeCount(prevCount);
      }
    } catch {
      setIsLiked(prevLiked);
      setLikeCount(prevCount);
    } finally {
      isLikingRef.current = false;
      setIsLiking(false);
    }
  };

  // ── Price ──────────────────────────────────────────────────────────────────
  const price = typeof product?.price === 'number'
    ? product.price
    : parseFloat(product?.price) || 0;

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div
      className={styles.card}
      onClick={handleCardClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* ── Media container ─────────────────────────────────────────────── */}
      <div className={styles.cardImageContainer}>

        {isVideo ? (
          <div style={{
            position: 'absolute', inset: 0, background: '#111',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <video
              ref={videoRef}
              src={resolvedSrc}
              style={{
                width: '100%', height: '100%',
                objectFit: 'contain', display: 'block',
                pointerEvents: 'none',
              }}
              muted={isMuted}
              playsInline
              loop
              preload="metadata"
              onError={(e) => { e.target.style.display = 'none'; }}
            />

            {/* Play icon (idle) */}
            {!isHovered && (
              <div style={{
                position: 'absolute', top: '50%', left: '50%',
                transform: 'translate(-50%, -50%)',
                width: 44, height: 44, borderRadius: '50%',
                background: 'rgba(0,0,0,0.55)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                pointerEvents: 'none',
              }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="white">
                  <path d="M8 5v14l11-7z"/>
                </svg>
              </div>
            )}

            {/* Audio controls (hover) */}
            {isHovered && (
              <div
                onClick={(e) => e.stopPropagation()}
                style={{
                  position: 'absolute', bottom: 0, left: 0, right: 0,
                  display: 'flex', alignItems: 'center', gap: 6, padding: '6px 10px',
                  background: 'linear-gradient(transparent, rgba(0,0,0,0.7))', zIndex: 10,
                }}
              >
                <button onClick={toggleMute} style={{
                  width: 28, height: 28, borderRadius: '50%',
                  background: 'rgba(255,255,255,0.15)',
                  border: '1px solid rgba(255,255,255,0.3)',
                  cursor: 'pointer', flexShrink: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }} title={isMuted ? 'Unmute' : 'Mute'}>
                  {isMuted ? (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="white">
                      <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"/>
                    </svg>
                  ) : (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="white">
                      <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
                    </svg>
                  )}
                </button>

                <input
                  type="range" min="0" max="1" step="0.05" value={volume}
                  onChange={handleVolumeChange}
                  onClick={(e) => e.stopPropagation()}
                  style={{ flex: 1, height: 3, cursor: 'pointer', accentColor: '#fff', opacity: 0.9 }}
                  title={`Volume: ${Math.round(volume * 100)}%`}
                />

                <span style={{
                  color: 'white', fontSize: 10, fontWeight: 600,
                  minWidth: 28, textAlign: 'right', flexShrink: 0,
                }}>
                  {Math.round(volume * 100)}%
                </span>
              </div>
            )}

            {/* VIDEO badge */}
            <div style={{
              position: 'absolute', top: 8, left: 8,
              background: 'rgba(0,0,0,0.55)', color: 'white',
              fontSize: 10, fontWeight: 700, padding: '2px 7px',
              borderRadius: 4, letterSpacing: '0.5px', pointerEvents: 'none',
            }}>
              VIDEO
            </div>
          </div>
        ) : (
          <img
            src={imgSrc}
            alt={product?.name || 'Product image'}
            onError={() => { if (!hasError) { setHasError(true); setImgSrc(FALLBACK_IMAGE); } }}
            loading="lazy"
          />
        )}

        {/* ── Like button ───────────────────────────────────────────────── */}
        <button
          className={`${styles.likeButton} ${isLiked ? styles.liked : ''}`}
          onClick={handleLikeClick}
          disabled={isLiking}
          aria-label={isLiked ? 'Unlike' : 'Like'}
          style={{ position: 'absolute', zIndex: 20 }}
        >
          <svg
            viewBox="0 0 24 24"
            className={styles.heartIcon}
            fill={isLiked ? 'currentColor' : 'none'}
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
          </svg>
          {likeCount > 0 && (
            <span className={styles.likeCount}>{likeCount}</span>
          )}
        </button>

      </div>

      {/* ── Card body ────────────────────────────────────────────────────── */}
      <div className={styles.cardBody}>
        <h4 className={styles.productTitle}>{product?.name || 'Untitled'}</h4>
        <div className={styles.price}>KES {price.toLocaleString()}</div>
        <div className={styles.productLocation}>📍 {product?.city || 'Location not set'}</div>

        {/* ── Verified Seller badge ──────────────────────────────────────── */}
        {product?.verified && (
          <div className={styles.verifiedSellerBadge}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="#16a34a" xmlns="http://www.w3.org/2000/svg">
              <path d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" stroke="#16a34a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
              <path d="M9 12l2 2 4-4" stroke="#16a34a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Verified Seller
          </div>
        )}

        <div className={styles.productRating}>
          {renderStars(rating ?? 0)}
          {rating !== null && rating > 0 ? (
            <span className={styles.ratingText}>
              ★{rating} ({ratingCount > 0 ? `${ratingCount} ${ratingCount === 1 ? 'review' : 'reviews'}` : 'rated'})
            </span>
          ) : (
            <span className={styles.ratingText}>No ratings yet</span>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductCard;